import React, { useEffect, useMemo, useState, useRef } from "react";
import { apiFetch, apiJson } from "../utils/api";
import SidePanel from "./SidePanel";
import "./Branches.css";

const DEFAULT_WORKING_START = "09:00";
const DEFAULT_WORKING_END = "18:00";

const emptyForm = {
  branchCode: "",
  branchName: "",
  registeredName: "",
  address: "",
  area: "",
  country: "",
  contactNumber: "",
  trnId: "",
  googleLocation: "",
  waPhoneNumberId: "",
  workingStartTime: DEFAULT_WORKING_START,
  workingEndTime: DEFAULT_WORKING_END,
  isActive: true
};

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const submitInFlightRef = useRef(false);
  const [recordAudit, setRecordAudit] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setRecordAudit(null);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setRecordAudit(null);
  };

  const loadBranches = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiJson("/api/branches");
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load branches", err);
      setError(err.message || "Could not load branches");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  const activeCount = useMemo(() => branches.filter(branch => branch.isActive).length, [branches]);

  const getCustomerBookingLink = branch => {
    const branchRef = String(branch?.publicId || branch?.id || "").trim();
    if (!branchRef || typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = `book?branch=${encodeURIComponent(branchRef)}`;
    return url.toString();
  };

  const updateField = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const submit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) return;
    
    submitInFlightRef.current = true;
    setIsSaving(true);

    const releaseSubmitLock = () => {
      submitInFlightRef.current = false;
      setIsSaving(false);
    };

    if (!form.branchCode.trim() || !form.branchName.trim()) {
      alert("Branch code and branch name are required.");
      releaseSubmitLock();
      return;
    }

    const payload = {
      branchCode: form.branchCode.trim().toUpperCase(),
      branchName: form.branchName.trim(),
      registeredName: form.registeredName.trim(),
      address: form.address.trim(),
      area: form.area.trim(),
      country: form.country.trim(),
      contactNumber: form.contactNumber.trim(),
      trnId: form.trnId.trim(),
      googleLocation: form.googleLocation.trim(),
      waPhoneNumberId: form.waPhoneNumberId.trim(),
      workingStartTime: form.workingStartTime || DEFAULT_WORKING_START,
      workingEndTime: form.workingEndTime || DEFAULT_WORKING_END,
      isActive: !!form.isActive
    };

    try {
      let saved;
      if (editingId) {
        saved = await apiJson(`/api/branches/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
        setBranches(prev => prev.map(branch => ((branch.publicId || branch.id) === editingId ? saved : branch)));
      } else {
        saved = await apiJson("/api/branches", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        setBranches(prev => [saved, ...prev]);
      }
      closePanel();
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to save branch", err);
      alert(err.message || "Could not save branch");
      releaseSubmitLock();
    }
  };

  const openEdit = branch => {
    setEditingId(branch.publicId || String(branch.id));
    setForm({
      branchCode: branch.branchCode || "",
      branchName: branch.branchName || "",
      registeredName: branch.registeredName || "",
      address: branch.address || "",
      area: branch.area || "",
      country: branch.country || "",
      contactNumber: branch.contactNumber || "",
      trnId: branch.trnId || "",
      googleLocation: branch.googleLocation || "",
      waPhoneNumberId: branch.waPhoneNumberId || "",
      workingStartTime: branch.workingStartTime || DEFAULT_WORKING_START,
      workingEndTime: branch.workingEndTime || DEFAULT_WORKING_END,
      isActive: !!branch.isActive
    });
    setRecordAudit({ createdBy: branch.createdBy || "", createdAt: branch.createdAt || "", updatedBy: branch.updatedBy || "", updatedAt: branch.updatedAt || "" });
    setPanelOpen(true);
  };

  const removeBranch = async branch => {
    const id = branch.publicId || String(branch.id);
    if (!id) return;
    if (!window.confirm(`Delete branch ${branch.branchCode || ""}?`)) return;

    try {
      const res = await apiFetch(`/api/branches/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error || `Delete failed (${res.status})`);
      }
      setBranches(prev => prev.filter(item => (item.publicId || item.id) !== id));
      if (editingId === id) {
        closePanel();
      }
    } catch (err) {
      console.error("Failed to delete branch", err);
      alert(err.message || "Could not delete branch");
    }
  };

  const copyCustomerBookingLink = async branch => {
    const link = getCustomerBookingLink(branch);
    if (!link) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        alert("Customer booking link copied.");
        return;
      }
    } catch (err) {
      console.error("Failed to copy booking link", err);
    }
    window.prompt("Copy this customer booking link", link);
  };

  const openCustomerBookingLink = branch => {
    const link = getCustomerBookingLink(branch);
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="branches-page page main-content">
      <div className="branches-header">
        <div>
          <h1>Branch Master</h1>
          <p>Maintain branch profiles, licensing details, and operational working hours.</p>
        </div>
        <div className="branches-actions">
          <div className="branches-summary">{`${activeCount} active branches / ${branches.length} total`}</div>
          <button className="open-panel-btn" type="button" onClick={openCreate}>Add Branch</button>
        </div>
      </div>

      <section className="branches-list-card">
        <h2>All Branches</h2>
        {loading ? <p>Loading branches...</p> : error ? <p className="branches-error">{error}</p> : (
          <table className="branches-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Registered Name</th>
                <th>Area</th>
                <th>Country</th>
                <th>Contact</th>
                <th>TRN</th>
                <th>Working Hours</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {branches.map(branch => (
                <tr key={branch.publicId || branch.id}>
                  <td data-label="Code">{branch.branchCode || "-"}</td>
                  <td data-label="Name">{branch.branchName || "-"}</td>
                  <td data-label="Registered Name">{branch.registeredName || "-"}</td>
                  <td data-label="Area">{branch.area || "-"}</td>
                  <td data-label="Country">{branch.country || "-"}</td>
                  <td data-label="Contact">{branch.contactNumber || "-"}</td>
                  <td data-label="TRN">{branch.trnId || "-"}</td>
                  <td data-label="Working Hours">{`${branch.workingStartTime || DEFAULT_WORKING_START} - ${branch.workingEndTime || DEFAULT_WORKING_END}`}</td>
                  <td data-label="Status">{branch.isActive ? "Active" : "Inactive"}</td>
                  <td data-label="Actions" className="branches-action-cell">
                    <button type="button" onClick={() => openEdit(branch)}>Edit</button>
                    <button type="button" className="secondary" onClick={() => copyCustomerBookingLink(branch)}>Copy Booking Link</button>
                    <button type="button" className="secondary" onClick={() => openCustomerBookingLink(branch)}>Open Booking Link</button>
                    <button type="button" className="danger" onClick={() => removeBranch(branch)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <SidePanel isOpen={panelOpen} onClose={closePanel} className="panel-medium">
        <div className="branches-form-container">
          <h2>{editingId ? "Edit Branch" : "Create New Branch"}</h2>
          <form onSubmit={submit} className="branches-form">
            <div className="branches-form-row two-col">
              <div>
                <label>Branch code *</label>
                <input name="branchCode" value={form.branchCode} onChange={updateField} required />
              </div>
              <div>
                <label>Branch name *</label>
                <input name="branchName" value={form.branchName} onChange={updateField} required />
              </div>
            </div>

            <div className="branches-form-row one-col">
              <div>
                <label>Registered name</label>
                <input name="registeredName" value={form.registeredName} onChange={updateField} placeholder="Legal / registered company name" />
              </div>
            </div>

            <div className="branches-form-row two-col">
              <div>
                <label>Area</label>
                <input name="area" value={form.area} onChange={updateField} />
              </div>
              <div>
                <label>Country</label>
                <input name="country" value={form.country} onChange={updateField} />
              </div>
            </div>

            <div className="branches-form-row two-col">
              <div>
                <label>Contact number</label>
                <input name="contactNumber" value={form.contactNumber} onChange={updateField} />
              </div>
              <div>
                <label>TRN ID</label>
                <input name="trnId" value={form.trnId} onChange={updateField} />
              </div>
            </div>

            <div className="branches-form-row one-col">
              <div>
                <label>Address</label>
                <input name="address" value={form.address} onChange={updateField} />
              </div>
            </div>

            <div className="branches-form-row one-col">
              <div>
                <label>Google location</label>
                <input name="googleLocation" value={form.googleLocation} onChange={updateField} placeholder="Paste Google Maps link or coordinates" />
              </div>
            </div>

            <div className="branches-form-row one-col">
              <div>
                <label>WhatsApp Phone Number ID</label>
                <input name="waPhoneNumberId" value={form.waPhoneNumberId} onChange={updateField} placeholder="e.g. 123456789012345 (from Meta Business Dashboard)" />
              </div>
            </div>

            <div className="branches-form-row two-col">
              <div>
                <label>Working start time</label>
                <input type="time" name="workingStartTime" value={form.workingStartTime} onChange={updateField} required />
              </div>
              <div>
                <label>Working end time</label>
                <input type="time" name="workingEndTime" value={form.workingEndTime} onChange={updateField} required />
              </div>
            </div>

            <div className="branches-active-row">
              <label>
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={updateField} /> Active branch
              </label>
            </div>

            {recordAudit && (recordAudit.createdBy || recordAudit.createdAt || recordAudit.updatedBy || recordAudit.updatedAt) && (
              <div className="audit-trail">
                {(recordAudit.createdBy || recordAudit.createdAt) && (
                  <span className="audit-item"><strong>Created by:</strong> {recordAudit.createdBy || "\u2014"}{recordAudit.createdAt ? ` \u00b7 ${new Date(recordAudit.createdAt).toLocaleString()}` : ""}</span>
                )}
                {(recordAudit.updatedBy || recordAudit.updatedAt) && (
                  <span className="audit-item"><strong>Updated by:</strong> {recordAudit.updatedBy || "\u2014"}{recordAudit.updatedAt ? ` \u00b7 ${new Date(recordAudit.updatedAt).toLocaleString()}` : ""}</span>
                )}
              </div>
            )}

            <div className="branches-form-actions">
              <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : (editingId ? "Save Branch" : "Create Branch")}</button>
              <button type="button" onClick={closePanel}>Cancel</button>
            </div>
          </form>
        </div>
      </SidePanel>
    </div>
  );
};

export default Branches;
